import React, { useEffect, useState } from 'react';
import { useSimasignadas, useInfoZona } from '../hooks/DashboardHook';
import ChartRadial from './RadialGrafica';

type Props = {
  filterDate: any;
  loadingGrid: boolean;
};

const SimAsig: React.FC<Props> = ({ filterDate, loadingGrid }) => {
  const [selectedZonaId, setSelectedZonaId] = useState<number | null>(() => {
    const saved = sessionStorage.getItem('selectedZonaId_global');
    return saved ? Number(saved) : null;
  });
  const [sendZonaId, setSendZonaId] = useState<boolean>(!!selectedZonaId);

  const [activadas, setActivadas] = useState(0);
  const [portadas, setPortadas] = useState(0);
  const [asignadasZona, setAsignadasZona] = useState(0);
  const [totalZona, setTotalZona] = useState(0);

  const [porcActivadas, setPorcActivadas] = useState(0);
  const [porcPortadas, setPorcPortadas] = useState(0);
  const [porcAsignadas, setPorcAsignadas] = useState(0);

  const [initValues, setInitValues] = useState({
    init_date: filterDate.init_date,
    end_date: filterDate.end_date,
  });

  const { data: asignadas, loading: loadingData } = useSimasignadas(
    true,
    initValues
  );
  const { data: zona, loading: loadingZona } = useInfoZona(
    sendZonaId,
    selectedZonaId || 0,
    initValues
  );

  useEffect(() => {
    setInitValues({
      init_date: filterDate.init_date,
      end_date: filterDate.end_date,
    });
  }, [filterDate]);

  useEffect(() => {
    if (asignadas?.length && !selectedZonaId) {
      const firstId = asignadas[0].zona_id;
      setSelectedZonaId(firstId);
      sessionStorage.setItem('selectedZonaId_global', String(firstId));
      setSendZonaId(true);
    }
  }, [asignadas, selectedZonaId]);

  useEffect(() => {
    if (zona?.cantidad_stocks !== undefined) {
      sessionStorage.setItem('zonaData_global', JSON.stringify(zona));

      const total = zona.cantidad_stocks ?? 0;
      const activadasVal = zona.totalActivadas ?? 0;
      const portadasVal = zona.totalPortadas ?? 0;
      const asignadasVal = zona.totalAsignadas ?? 0;

      setTotalZona(total);
      setActivadas(activadasVal);
      setPortadas(portadasVal);
      setAsignadasZona(asignadasVal);

      const calcPct = (v: number) => (total > 0 ? (v / total) * 100 : 0);
      setPorcActivadas(calcPct(activadasVal));
      setPorcPortadas(calcPct(portadasVal));
      setPorcAsignadas(calcPct(asignadasVal));
    }
  }, [zona]);

  useEffect(() => {
    const storedZona = sessionStorage.getItem('zonaData_global');
    if (storedZona && !zona) {
      const parsed = JSON.parse(storedZona);
      setTotalZona(parsed.cantidad_stocks ?? 0);
      setActivadas(parsed.totalActivadas ?? 0);
      setPortadas(parsed.totalPortadas ?? 0);
      setAsignadasZona(parsed.totalAsignadas ?? 0);

      const total = parsed.cantidad_stocks ?? 0;
      const calcPct = (v: number) => (total > 0 ? (v / total) * 100 : 0);
      setPorcActivadas(calcPct(parsed.totalActivadas ?? 0));
      setPorcPortadas(calcPct(parsed.totalPortadas ?? 0));
      setPorcAsignadas(calcPct(parsed.totalAsignadas ?? 0));
    }
  }, []);

  const handleZonaClick = (id: number) => {
    setSelectedZonaId(id);
    sessionStorage.setItem('selectedZonaId_global', String(id));
    setSendZonaId(false);
    setTimeout(() => setSendZonaId(true), 0);
  };

  const loading =
    loadingGrid ||
    loadingData ||
    loadingZona ||
    (!zona && !sessionStorage.getItem('zonaData_global'));

  const maxVisible = 10;
  const totalZonas = asignadas?.length || 0;

  return (
    <div className="card card-flush w-100 h-550px ms-2">
      <div className="card-header pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold text-dark fs-3">
            Monitoreo de SIM asignadas
          </span>
          <span className="text-gray-400 mt-1 fw-semibold fs-6">
            Identificación por zonas
          </span>
        </h3>
      </div>

      <div className="card-body pt-0">
        {loading ? (
          <div className="d-flex align-items-center">
            <div className="text-muted text-center fs-6 w-100 m-5">
              <span className="indicator-progress" style={{ display: 'block' }}>
                Espere por favor...
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </div>
          </div>
        ) : (
          <div className="row">
            <div className="col-7">
              <div
                style={{
                  maxHeight: '350px',
                  overflowY: totalZonas > maxVisible ? 'auto' : 'visible',
                  marginTop: '1rem',
                  paddingRight: '4px',
                }}
              >
                {asignadas.length === 0 ? (
                  <div className="text-muted text-center py-4">
                    Sin registros
                  </div>
                ) : (
                  <>
                    <div className="d-flex fw-bold border-bottom px-3 py-2">
                      <div style={{ width: '70%' }}>Zonas</div>
                      <div style={{ width: '20%', textAlign: 'right' }}>
                        Totales
                      </div>
                    </div>

                    {asignadas.map((row) => {
                      const isSelected = selectedZonaId === row.zona_id;
                      return (
                        <div
                          key={row.zona_id}
                          onClick={() => handleZonaClick(row.zona_id)}
                          className={`d-flex align-items-center px-3 py-2 ${
                            isSelected
                              ? 'bg-light-primary fw-bolder text-primary'
                              : 'text-muted'
                          }`}
                          style={{
                            cursor: 'pointer',
                            borderRadius: '6px',
                            transition: 'all 0.2s ease-in-out',
                          }}
                        >
                          <div
                            style={{
                              width: '70%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.zona_nombre}
                          </div>
                          <div style={{ width: '20%', textAlign: 'right' }}>
                            {row.cantidad_stocks}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
              {totalZonas > 0 && (
                <div className="text-center text-gray-500 mt-3 small fw-semibold">
                  Mostrando {totalZonas} zonas
                </div>
              )}
            </div>

            <div className="col-5">
              {zona ? (
                <>
                  <ChartRadial
                    value={porcActivadas}
                    total={activadas}
                    operation={'Activadas'}
                    color="#7A4EEA"
                    height={150}
                  />
                  <ChartRadial
                    value={porcPortadas}
                    total={portadas}
                    operation={'Portadas'}
                    color="#F26522"
                    height={150}
                  />
                  <ChartRadial
                    value={porcAsignadas}
                    total={asignadasZona}
                    operation={'Asignadas'}
                    color="#0d6efd"
                    height={150}
                  />
                </>
              ) : (
                <div className="text-center text-muted mt-5">
                  Sin datos de zona
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { SimAsig };
